"use client";

import { useQuery } from "@tanstack/react-query";
import { NOTIFICATIONS_REFETCH_INTERVAL_MS } from "@/features/notifications/constants";
import { parseNumericLastSelectedId } from "@/lib/pagination/scrollPageUrl";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { NotificationsApi } from "@/openapi/generated";
import { useNotificationsRefetchInterval } from "./useNotificationsRefetchInterval";

interface UseNotificationsUnreadGetProps {
  userId: number;
  page?: number;
  pageSize?: number;
  lastSelectedId?: string | number;
  active?: boolean;
  refetchInterval?: number | false;
}

export const useNotificationsUnreadGet = ({
  userId,
  page = 1,
  pageSize = 10,
  lastSelectedId,
  active,
  refetchInterval = NOTIFICATIONS_REFETCH_INTERVAL_MS,
}: UseNotificationsUnreadGetProps) => {
  const accessToken = useAccessToken();
  const effectiveRefetchInterval =
    useNotificationsRefetchInterval(refetchInterval);

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch } = useQuery({
    queryKey: [
      QueryKeys.NotificationsUnread,
      userId,
      page,
      pageSize,
      lastSelectedId,
      active,
    ],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(NotificationsApi, accessToken);
      return service.apiNotificationsUserIdUnreadGet({
        userId,
        page,
        pageSize,
        lastSelectedId: parseNumericLastSelectedId(lastSelectedId),
        active,
      });
    },
    enabled: !!accessToken && !!userId,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 30 * 1000,
    refetchInterval: effectiveRefetchInterval,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  };
};
